import { useEffect } from "react";
import { mediaFileUrl, type MediaRow } from "../api.ts";

const VIDEO_EXTS = new Set(["mp4", "webm", "mkv", "mov", "avi", "m4v"]);

function isVideo(filename: string): boolean {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return VIDEO_EXTS.has(ext);
}

export function MediaPlayerModal({
  project,
  media,
  onClose,
}: {
  project: string;
  media: MediaRow;
  onClose: () => void;
}) {
  const src = mediaFileUrl(project, media.storedName);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="truncate text-sm font-medium text-slate-200" title={media.filename}>
            {media.filename}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-indigo-400 hover:underline"
            >
              Open in new tab
            </a>
            <button
              onClick={onClose}
              aria-label="Close player"
              className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            >
              ✕
            </button>
          </div>
        </div>

        {isVideo(media.filename) ? (
          <video
            src={src}
            controls
            autoPlay
            className="max-h-[70vh] w-full rounded-lg bg-black"
          />
        ) : (
          <audio src={src} controls autoPlay className="w-full" />
        )}
      </div>
    </div>
  );
}
